import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';

import {
	Company,
	Filter,
	SharedStats,
	SortType,
	TagWithCount,
	Mode,
	CompanySearchResult,
	AggregateAssetResult,
	AssetSearchResult,
	Asset,
	SearchResult,
	SortDetails,
} from 'company-finder-common';

import { CompanyService } from '../company/company.service.js';
import { DeploymentContext } from '../../utilities/deployment-context/deployment-context.js';
import { ServiceBase } from '../_service.base.js';

import _ from 'lodash-es';
import { AssetService } from '../asset/asset.service.js';

@Injectable({ providedIn: 'root' })
export class SearchService extends ServiceBase {
	public readonly PAGE_SIZE = 20;
	public readonly ARRAY_QUERY_PARAM_DELIMITER = '+';

	public filterSubject: Subject<Mode> = new Subject();
	public drilldownSubject: Subject<Mode> = new Subject();
	public badSearchPredicate: string;
	public badSearchPredicateSubject: Subject<string> = new Subject<string>();
	public searchPredicateSubject: Subject<string> = new Subject<string>();
	public searchResultsSubject: Subject<number> = new Subject<number>();
	public initialFetchSize: number = undefined;

	private _companies: Company[];
	private _assets: Asset[];
	private _totalCompanyCount: number;
	private _totalAssetCount: number;
	private _tagCounts: TagWithCount[];
	public currentCompanySearchPredicate: string = '';
	public currentAssetSearchPredicate: string = '';
	private _lastCompanySearchTotal: number = 0;
	private _lastAssetSearchTotal: number = 0;

	private _lastCompanySearchPredicate: string;
	private _lastAssetSearchPredicate: string;
	private _lastCompanySearchFilter: Filter;
	private _lastAssetSearchFilter: Filter;

	public get totalCount(): number {
		switch (this._context.currentMode) {
			case Mode.Asset:
				return this._totalAssetCount;
			case Mode.Company:
			default:
				return this._totalCompanyCount;
		}
	}

	public set totalCount(value: number) {
		switch (this._context.currentMode) {
			case Mode.Asset:
				this._totalAssetCount = value;
				break;
			case Mode.Company:
			default:
				this._totalCompanyCount = value;
				break;
		}
	}

	public get lastSearchFilter(): Filter {
		switch (this._context.currentMode) {
			case Mode.Asset:
				return this._lastAssetSearchFilter;
			case Mode.Company:
			default:
				return this._lastCompanySearchFilter;
		}
	}

	public set lastSearchFilter(value: Filter) {
		switch (this._context.currentMode) {
			case Mode.Asset:
				this._lastAssetSearchFilter = value;
				break;
			case Mode.Company:
			default:
				this._lastCompanySearchFilter = value;
				break;
		}
	}

	public get lastSearchPredicate(): string {
		switch (this._context.currentMode) {
			case Mode.Asset:
				return this._lastAssetSearchPredicate;
			case Mode.Company:
			default:
				return this._lastCompanySearchPredicate;
		}
	}

	public set lastSearchPredicate(value: string) {
		switch (this._context.currentMode) {
			case Mode.Asset:
				this._lastAssetSearchPredicate = value;
				break;
			case Mode.Company:
			default:
				this._lastCompanySearchPredicate = value;
				break;
		}
	}

	public get currentSearchPredicate(): string {
		switch (this._context.currentMode) {
			case Mode.Asset:
				return this.currentAssetSearchPredicate;
			case Mode.Company:
			default:
				return this.currentCompanySearchPredicate;
		}
	}

	public set currentSearchPredicate(value: string) {
		switch (this._context.currentMode) {
			case Mode.Asset:
				this.currentAssetSearchPredicate = value ?? '';
				break;
			case Mode.Company:
			default:
				this.currentCompanySearchPredicate = value ?? '';
				break;
		}
	}

	public companyFilter: Filter = new Filter({
		...Filter.emptyAlphaSortFilter,
		mode: Mode.Company,
	});

	public assetFilter: Filter = new Filter({
		...Filter.emptyAlphaSortFilter,
		primarySectors: [this._context.sectors[0].name],
		mode: Mode.Asset,
	});

	public clearAllFilters(preserveSingleAssetSector: boolean) {
		// If the asset filter has any sectors, preserve exactly one of them.
		// If it does not, default to the first sector.
		this.assetFilter.clear(preserveSingleAssetSector);
		if (
			!this.assetFilter.primarySectors ||
			this.assetFilter.primarySectors.length === 0 ||
			!this.assetFilter.primarySectors[0]
		) {
			this.assetFilter.primarySectors = [this._context.sectorNames[0]];
		}
		// The caller argument is specific to Assets, so always invoke this with the default (false)
		this.companyFilter.clear();
	}

	public get filter(): Filter {
		switch (this._context.currentMode) {
			case Mode.Asset:
				return this.assetFilter;
			case Mode.Company:
			default:
				return this.companyFilter;
		}
	}

	public set filter(value: Filter) {
		if (!value.sort) {
			value.sort = SortDetails.AlphaSort;
		}
		switch (this._context.currentMode) {
			case Mode.Asset:
				this.assetFilter = value;
				this.assetFilter.mode = Mode.Asset;
				break;
			case Mode.Company:
			default:
				this.companyFilter = value;
				this.companyFilter.mode = Mode.Company;
				break;
		}
	}

	constructor(
		private companyService: CompanyService,
		private assetService: AssetService,
		context: DeploymentContext,
		httpClient: HttpClient,
		private router: Router
	) {
		super(httpClient, context, '/search');
	}

	public get companies(): Company[] {
		return this._companies;
	}

	/** The count of companies included in the most recent search scope
	 * (if multiple searches are performed concurrently, there is no guarantee which one this value will represent) */
	// FUTURE: There is probably a better way to have exposed the count of companies in the last search to webanalytics calls
	//  that didn't easily have a handle on the current search results.
	public get companyCountFromLastSearch(): number {
		return this._totalCompanyCount;
	}

	public get isAllItems(): boolean {
		// A valid (i.e. non-empty and non-bad) search term or filter term will reduce the scope
		// of the search to not include all companies. Though it's theoretically possible a search
		// term could match all companies, we don't consider that case
		return (
			(!this.currentSearchPredicate || !!this.badSearchPredicate) &&
			this.filter.isShowAll()
		);
	}

	public getSearchTermForAnalytics(): string {
		if (
			_.isEmpty(this.currentSearchPredicate) &&
			_.isEmpty(this.filter.locations) &&
			_.isEmpty(this.filter.primarySectors) &&
			_.isEmpty(this.filter.primarySubSectors) &&
			_.isEmpty(this.filter.secondarySectors) &&
			_.isEmpty(this.filter.secondarySubSectors) &&
			this.filter.locationStatuses === ''
		) {
			return null;
		}

		let searchString = '';
		if (!_.isEmpty(this.currentSearchPredicate)) {
			searchString += ' terms: ' + this.currentSearchPredicate;
		}

		if (!_.isEmpty(this.filter.locations)) {
			searchString += ' locations: ' + this.filter.locations;
		}

		if (!_.isEmpty(this.filter.primarySectors)) {
			searchString += ' primarySectors: ' + this.filter.primarySectors;
		}

		if (!_.isEmpty(this.filter.primarySubSectors)) {
			searchString +=
				' primarySubSectors: ' + this.filter.primarySubSectors;
		}

		if (this.filter.locationStatuses !== '') {
			searchString += ' location status: ' + this.filter.locationStatuses;
		}

		if (this.filter.isCrossSector !== null) {
			searchString += ' isCrossSector: ' + this.filter.isCrossSector;
		}

		if (this.filter.isQFCWinner !== null) {
			searchString += ' isQFCWinner: ' + this.filter.isQFCWinner;
		}

		if (this.filter.isNewInLastQuarter !== null) {
			searchString +=
				' isNewInLastQuarter: ' + this.filter.isNewInLastQuarter;
		}

		if (this.filter.currentRdStage !== null) {
			searchString += ' currentRdStage: ' + this.filter.currentRdStage;
		}

		if (this.filter.isNewInLast90Days !== null) {
			searchString +=
				' isNewInLast90Days: ' + this.filter.isNewInLast90Days;
		}

		if (this.filter.isFollowedCompaniesOnly !== null) {
			searchString +=
				' isFollowedCompaniesOnly: ' +
				this.filter.isFollowedCompaniesOnly;
		}

		if (this.filter.isFollowedAssetsOnly !== null) {
			searchString +=
				' isfollowedAssetsOnly: ' + this.filter.isFollowedAssetsOnly;
		}

		if (this.filter.isMyJPALCompaniesOnly !== null) {
			searchString +=
				' isMyJPALCompaniesOnly: ' + this.filter.isMyJPALCompaniesOnly;
		}

		if (searchString.length <= 60) {
			return searchString;
		} else {
			return searchString.substr(0, 60) + '...';
		}
	}

	public clearSearchPredicate(): void {
		this.currentSearchPredicate = undefined;
		this.searchPredicateSubject.next(this.currentSearchPredicate);
		this.searchResultsSubject.next(0);
	}

	public async getCompanies(
		from: number,
		size: number = this.PAGE_SIZE
	): Promise<CompanySearchResult> {
		// if we have already performed this search, don't repeat it
		if (
			this.currentCompanySearchPredicate &&
			this.currentCompanySearchPredicate ===
				this._lastCompanySearchPredicate &&
			from === 0 &&
			Filter.equals(this.filter, this._lastCompanySearchFilter)
		) {
			return {
				items: this._companies,
				totalItems: this._lastCompanySearchTotal,
			};
		}

		if (from === 0) {
			this._companies = [];
		}
		const result = await this.searchCompanies(from, size);

		this._companies = this._companies.concat(result.items);
		this._lastCompanySearchTotal = result.totalItems;

		return {
			items: this._companies,
			totalItems: result.totalItems,
		};
	}

	public async getAssets(
		from: number,
		size: number = this.PAGE_SIZE
	): Promise<AssetSearchResult> {
		if (
			this.currentAssetSearchPredicate &&
			this.currentAssetSearchPredicate ===
				this._lastAssetSearchPredicate &&
			from === 0 &&
			Filter.equals(this.filter, this._lastAssetSearchFilter)
		) {
			return {
				items: this._assets,
				totalItems: this._lastAssetSearchTotal,
			};
		}

		if (from === 0) {
			this._assets = [];
		}

		this.prepareForSearch();

		const result = await this.assetService.searchAndFilter(
			this.currentSearchPredicate,
			from,
			size,
			this.assetFilter
		);

		this._assets = this._assets.concat(result.items);
		this._lastCompanySearchTotal = result.totalItems;

		return {
			items: this._assets,
			totalItems: result.totalItems,
		};
	}

	public getSearchResultsQueryParams(): unknown {
		// Get the filter object, serialized into key=value pairs
		const serializedQueryParams =
			this.filter?.serializeToKeyValuePairs() ?? {};

		// Set up the queryParams from the filter, encoding them along the way.
		const queryParamsObj = {
			queryParams: {
				// An empty filter won't be in the URL, so ensure we have the
				// mode accessible
				mode: this._context.currentMode,
			},
		};

		// Similar to AssetService.searchAndFilter(), we no longer attempt to exclude "default" values
		if (this.filter?.sort?.direction) {
			queryParamsObj.queryParams['sortDirection'] =
				this.filter.sort.direction;
		}
		if (this.filter?.sort?.type) {
			queryParamsObj.queryParams['sortType'] = this.filter.sort.type;
		}
		if (this.filter?.sort?.field) {
			queryParamsObj.queryParams['sortField'] = this.filter.sort.field;
		}

		Object.keys(serializedQueryParams).forEach((key) => {
			const obj = serializedQueryParams[key];
			let value = obj.value;

			if (obj.isArray) {
				value = obj.value.join(this.ARRAY_QUERY_PARAM_DELIMITER);
			} else if (!obj.isObject) {
				// Complex objects won't encode properly and should be handled out of band
				value = encodeURIComponent(value);
			}

			queryParamsObj.queryParams[encodeURIComponent(key)] = value;
		});

		// Add the search predicate
		if (this.currentSearchPredicate) {
			queryParamsObj.queryParams['text'] = encodeURIComponent(
				this.currentSearchPredicate
			);
		}

		return queryParamsObj;
	}

	public async getTagCounts(): Promise<TagWithCount[]> {
		// FUTURE: Is there ever a situation where we want to update the tag counts?
		if (!this._tagCounts) {
			this._tagCounts = await this.companyService.getTagCounts();
		}
		return this._tagCounts;
	}

	public async getTypeahead(prefix: string): Promise<string[]> {
		return this.get<string[]>(`${this._apiUrl}/typeahead/${prefix}`);
	}

	public navigateToSearchResults(): void {
		this.router.navigate(
			['/search-results'],
			this.getSearchResultsQueryParams()
		);
	}

	private prepareForSearch() {
		this.currentSearchPredicate = this.currentSearchPredicate?.trim();

		if (!this.filter.sort) {
			// If there is no sort, default to Alpha
			this.filter.sort = SortDetails.AlphaSort;
		} else if (
			this.filter.sort.type === SortType.Ranking &&
			!this.currentSearchPredicate
		) {
			// If we are currently sorted on Rank but there is no search predicate, switch back to Alpha
			this.filter.sort = SortDetails.AlphaSort;
		}

		this.searchPredicateSubject.next(this.currentSearchPredicate);
	}

	public async searchCompanies(
		from: number = 0,
		size: number = this.PAGE_SIZE
	): Promise<CompanySearchResult> {
		this.prepareForSearch();
		let result = await this.companyService.searchAndFilter(
			this.currentSearchPredicate,
			from,
			size,
			this.filter
		);
		if (!result.items || result.items.length === 0) {
			// Notify subscribers there was a bad search term, then get all companies.
			this.badSearchPredicateSubject.next(this.currentSearchPredicate);
			result = await this.companyService.searchAndFilter(
				'',
				0,
				this.PAGE_SIZE,
				this.filter
			);
		} else {
			this.badSearchPredicateSubject.next(null);
		}

		this.finishSearch(result);
		return result;
	}
	public async searchAssetAggregateForFilter(
		filter: Filter
	): Promise<AggregateAssetResult> {
		return this.assetService.searchAndFilterAggregate(filter);
	}

	public async searchAssetAggregate(): Promise<AggregateAssetResult> {
		return this.assetService.searchAndFilterAggregate(this.filter);
	}

	public async searchAssetsForCsv(fields: string[]): Promise<ArrayBuffer> {
		return this.post(
			`${this._apiUrl}/asset/csv`,
			{
				prdicate: this.currentAssetSearchPredicate,
				filter: this.assetFilter,
				fields,
			},
			{
				responseType: 'arraybuffer',
			}
		);
	}

	public async searchAssetsForPdf(fields: string[]): Promise<ArrayBuffer> {
		return this.post(
			`${this._apiUrl}/asset/pdf`,
			{
				prdicate: this.currentAssetSearchPredicate,
				filter: this.assetFilter,
				fields,
			},
			{
				responseType: 'arraybuffer',
			}
		);
	}

	public async searchCompanyAggregate(): Promise<SharedStats> {
		this.prepareForSearch();
		let result = await this.companyService.searchAndFilterAggregate(
			this.filter,
			this.currentSearchPredicate
		);
		if (!result || result.numberOfCompanies === 0) {
			// Notify subscribers there was a bad search term, then get all companies.
			this.badSearchPredicateSubject.next(this.currentSearchPredicate);
			result = await this.companyService.filterAggregate(this.filter);
		} else {
			this.badSearchPredicateSubject.next(null);
		}

		this.finishSearch({
			totalItems: result.numberOfCompanies,
			items: [],
		});
		return result;
	}

	private finishSearch(result: SearchResult<unknown>) {
		this.totalCount = result.totalItems;

		if (result.items?.length > 0) {
			this.searchResultsSubject.next(this.totalCount);
		}

		this.lastSearchPredicate = this.currentSearchPredicate;
		// this.filter is mutable, so make sure this._lastSearchFilter isn't pointing to the same instance as this.filter
		this.lastSearchFilter = this.filter.clone();
	}

	public clearCachedSearch(): void {
		// Implemented as part of ADJQ-487 to facilitate making a fresh search (rather than returning cached results)
		// even if the search criteria has not changed
		this.lastSearchFilter = undefined;
		this.lastSearchPredicate = undefined;
		this._lastCompanySearchTotal = undefined;
	}
}
